import React, { useEffect, useState } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Badge, Card, CardHead } from '../../components/ui';
import { LoadingState, EmptyState, ErrorState } from '../../features/shared/PageStates';
import { STAGE_LABELS, STAGE_BADGE } from '../../lib/statusMaps';
import { formatZAR, formatDate, humanize } from '../../lib/format';

function QueueTable({ title, sub, loader, onNavigate, showToast, tab }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    loader()
      .then(res => setRows(res.deals || res.items || res.queue || []))
      .catch(e => {
        setError(e.message);
        showToast?.(e.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <LoadingState label={`Loading ${title}…`} />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <Card>
      <CardHead title={title} sub={sub} action actionLabel="Refresh" onAction={load} />
      {rows.length === 0 ? (
        <div className="card-body">
          <EmptyState title="Queue is empty" sub="No deals waiting in this queue." />
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>Ref</th>
              <th>Client</th>
              <th>End-client</th>
              <th>Capital</th>
              <th>Stage</th>
              <th>Sub-status</th>
              <th>Due / updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(d => (
              <tr
                key={d.id}
                className="clickable"
                onClick={() => onNavigate('dm-deal-detail', { dmDealId: d.id, dmTab: tab })}
              >
                <td className="fw mono">{d.deal_ref}</td>
                <td>{d.client_company_name}</td>
                <td>{d.end_client_name || '—'}</td>
                <td className="r">{formatZAR(d.capital_required)}</td>
                <td><Badge type={STAGE_BADGE[d.stage] || 'neutral'}>{STAGE_LABELS[d.stage] || d.stage}</Badge></td>
                <td>{humanize(d.sub_status)}</td>
                <td>{formatDate(d.next_action_due || d.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

export function IcQueuePage(props) {
  return (
    <QueueTable
      title="IC queue"
      sub="Deals awaiting investment committee"
      loader={dealMgmt.queueIc}
      tab="IC"
      {...props}
    />
  );
}

export function TlQcQueuePage(props) {
  return (
    <QueueTable
      title="Team Lead QC queue"
      sub="Deals awaiting TL quality check"
      loader={dealMgmt.queueTlQc}
      tab="Team Lead QC"
      {...props}
    />
  );
}

export function FinanceQueuePage(props) {
  return (
    <QueueTable
      title="Finance queue"
      sub="Handover & supplier payment"
      loader={dealMgmt.queueFinance}
      tab="Finance"
      {...props}
    />
  );
}

export function ProjectsPage(props) {
  return (
    <QueueTable
      title="Projects / delivery"
      sub="Delivery monitoring queue"
      loader={dealMgmt.queueDelivery}
      tab="Delivery"
      {...props}
    />
  );
}

export function RepaymentQueuePage(props) {
  return (
    <QueueTable
      title="Repayment queue"
      sub="Collections & receipts"
      loader={dealMgmt.queueRepayment}
      tab="Repayment"
      {...props}
    />
  );
}

export function ReconciliationQueuePage(props) {
  return (
    <QueueTable
      title="Reconciliation queue"
      sub="Close-out readiness"
      loader={dealMgmt.queueReconciliation}
      tab="Close-Out"
      {...props}
    />
  );
}

export function CapitalQueuePage(props) {
  return (
    <QueueTable
      title="Capital allocation queue"
      sub="Deals awaiting funding lines"
      loader={dealMgmt.capitalAllocationQueue}
      tab="Capital Allocation"
      {...props}
    />
  );
}
